import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ==============================
// Utility Helpers
// ==============================
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const uid = (prefix = '') => `${prefix}${Math.random().toString(36).slice(2, 9)}`;
const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// ==============================
// Small Reusable Subcomponents
// ==============================
const Sparkline = ({ values = [], height = 48, width = 120 }) => {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block align-middle">
      <polyline points={points} fill="none" strokeWidth="2" className="stroke-current text-cyan-500" />
      <circle cx={width} cy={height - ((values[values.length-1] - min) / range) * height} r="3" fill="#06b6d4" />
    </svg>
  );
};

const ProgressBar = ({ value = 0, label = '', compact = false, animated = false }) => {
  const progressRef = useRef(null);
  
  useEffect(() => {
    if (animated && progressRef.current) {
      progressRef.current.style.width = '0%';
      setTimeout(() => {
        if (progressRef.current) {
          progressRef.current.style.width = `${clamp(value, 0, 100)}%`;
        }
      }, 100);
    }
  }, [value, animated]);

  return (
    <div className={`w-full ${compact ? 'text-xs' : 'text-sm'}`} aria-hidden>
      <div className="flex justify-between mb-1">
        <span className="text-gray-700 truncate">{label}</span>
        <span className="text-gray-500">{Math.round(value)}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          ref={progressRef}
          className={`h-full bg-gradient-to-r from-cyan-500 to-blue-500 ${animated ? 'transition-all duration-700 ease-out' : ''}`}
          style={{ width: animated ? '0%' : `${clamp(value, 0, 100)}%` }}
        />
      </div>
    </div>
  );
};

const AnimatedCounter = ({ to = 0, duration = 800, format = (v) => v, className = '', suffix = '', prefix = '' }) => {
  const [value, setValue] = useState(0);
  
  useEffect(() => {
    let start = performance.now();
    const from = 0;
    const raf = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const cur = Math.round(from + (to - from) * p);
      setValue(cur);
      if (p < 1) requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }, [to, duration]);
  
  return <span className={className}>{prefix}{format(value)}{suffix}</span>;
};

const SkillRadarChart = ({ skills = [], size = 200 }) => {
  if (!skills.length) return null;
  
  const maxValue = Math.max(...skills.map(s => s.level), 100);
  const center = size / 2;
  const radius = size / 2 - 20;
  const angleStep = (Math.PI * 2) / skills.length;
  
  const points = skills.map((skill, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const valueRadius = (skill.level / maxValue) * radius;
    return {
      x: center + Math.cos(angle) * valueRadius,
      y: center + Math.sin(angle) * valueRadius,
      label: skill.name
    };
  });
  
  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  return (
    <div className="relative">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
        <circle cx={center} cy={center} r={radius * 0.25} fill="none" stroke="#d1d5db" strokeWidth="0.5" />
        <circle cx={center} cy={center} r={radius * 0.5} fill="none" stroke="#d1d5db" strokeWidth="0.5" />
        <circle cx={center} cy={center} r={radius * 0.75} fill="none" stroke="#d1d5db" strokeWidth="0.5" />
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#d1d5db" strokeWidth="0.5" />
        
        {skills.map((_, i) => {
          const angle = i * angleStep;
          const x1 = center + Math.cos(angle) * radius;
          const y1 = center + Math.sin(angle) * radius;
          return (
            <line key={i} x1={center} y1={center} x2={x1} y2={y1} stroke="#d1d5db" strokeWidth="0.5" />
          );
        })}
        
        <polygon points={polygonPoints} fill="rgba(6, 182, 212, 0.1)" stroke="#06b6d4" strokeWidth="2" />
        
        {points.map((point, i) => (
          <circle key={i} cx={point.x} cy={point.y} r="4" fill="#06b6d4" className="cursor-pointer" />
        ))}
      </svg>
      
      <div className="absolute inset-0">
        {skills.map((skill, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const labelRadius = radius + 15;
          const x = center + Math.cos(angle) * labelRadius;
          const y = center + Math.sin(angle) * labelRadius;
          
          return (
            <div key={i} className="absolute text-xs text-gray-600 transform -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}px`, top: `${y}px` }}>
              {skill.name}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const scaleHover = { 
  whileHover: { scale: 1.03, transition: { duration: 0.2 } }, 
  whileTap: { scale: 0.98 } 
};

// ==============================
// Hardcoded Data
// ==============================
const leetcodeStats = {
  totalSolved: 187,
  easy: 97,
  medium: 76,
  hard: 14,
  streak: 60,
  ranking: 'Top 15%',
  languages: ['Python', 'C++', 'Java'],
  accuracy: 92.5,
  submissions: 625,
  contests: 1,
  history: [12, 8, 10, 14, 9, 11, 13, 15, 9, 8],
  activity: [
    { day: 'Mon', count: 8 },
    { day: 'Tue', count: 12 },
    { day: 'Wed', count: 6 },
    { day: 'Thu', count: 10 },
    { day: 'Fri', count: 14 },
    { day: 'Sat', count: 18 },
    { day: 'Sun', count: 9 }
  ]
};

const gfgStats = {
  totalSolved: 77,
  school: 0,
  basic: 30,
  easy: 36,
  medium: 10,
  hard: 1,
  streak: 37,
  ranking: 'Top 10%',
  codingScore: 1250,
  articles: 8,
  contests: 5,
  history: [15, 12, 18, 22, 20, 25, 28, 30, 32, 35],
  activity: [
    { day: 'Mon', count: 12 },
    { day: 'Tue', count: 15 },
    { day: 'Wed', count: 10 },
    { day: 'Thu', count: 18 },
    { day: 'Fri', count: 22 },
    { day: 'Sat', count: 25 },
    { day: 'Sun', count: 20 }
  ]
};

const naukriStats = {
  totalSolved: 267,
  easy: 250,
  medium: 15,
  hard: 2,
  streak: 58,
  ranking: 'Top 8%',
  accuracy: 88.5,
  certifications: 12,
  challenges: 24,
  history: [25, 28, 32, 35, 38, 42, 45, 48, 50, 55],
  activity: [
    { day: 'Mon', count: 15 },
    { day: 'Tue', count: 18 },
    { day: 'Wed', count: 14 },
    { day: 'Thu', count: 22 },
    { day: 'Fri', count: 28 },
    { day: 'Sat', count: 32 },
    { day: 'Sun', count: 25 }
  ]
};

const achievements = [
  { id: uid('ach_'), title: 'Years of Experience', count: 4, icon: '💼', description: 'Professional experience across multiple domains' },
  { id: uid('ach_'), title: 'Total Problems Solved', count: leetcodeStats.totalSolved + gfgStats.totalSolved + naukriStats.totalSolved, icon: '💻', description: 'Across LeetCode, GFG & Naukri' },
  { id: uid('ach_'), title: 'Research Papers', count: 3, icon: '📚', description: 'Published research in ML and Data Science' },
  { id: uid('ach_'), title: 'Hackathons Won', count: 5, icon: '🏆', description: 'Competitive programming and innovation challenges' },
  { id: uid('ach_'), title: 'Open Source Contributions', count: 12, icon: '🌟', description: 'Active contributions to open source projects' }
];

const skillsData = [
  { category: 'Programming', skills: [
    { name: 'Python', level: 95 },
    { name: 'JavaScript', level: 90 },
    { name: 'C++', level: 85 },
    { name: 'Java', level: 80 },
    { name: 'TypeScript', level: 88 }
  ]},
  { category: 'Frontend', skills: [
    { name: 'React', level: 92 },
    { name: 'Next.js', level: 85 },
    { name: 'Tailwind CSS', level: 90 },
    { name: 'Redux', level: 88 },
    { name: 'HTML/CSS', level: 95 }
  ]},
  { category: 'Backend', skills: [
    { name: 'Node.js', level: 90 },
    { name: 'Express', level: 88 },
    { name: 'Django', level: 85 },
    { name: 'Flask', level: 82 },
    { name: 'GraphQL', level: 78 }
  ]},
  { category: 'Data & ML', skills: [
    { name: 'Pandas', level: 90 },
    { name: 'NumPy', level: 88 },
    { name: 'TensorFlow', level: 85 },
    { name: 'PyTorch', level: 82 },
    { name: 'Scikit-Learn', level: 87 }
  ]},
  { category: 'DevOps', skills: [
    { name: 'Docker', level: 85 },
    { name: 'Kubernetes', level: 78 },
    { name: 'AWS', level: 82 },
    { name: 'CI/CD', level: 80 },
    { name: 'Terraform', level: 75 }
  ]},
  { category: 'Databases', skills: [
    { name: 'MongoDB', level: 88 },
    { name: 'PostgreSQL', level: 85 },
    { name: 'MySQL', level: 82 },
    { name: 'Redis', level: 80 },
    { name: 'Elasticsearch', level: 75 }
  ]}
];

const tabs = [
  { id: 'leetcode', label: 'LeetCode', icon: '💻' },
  { id: 'gfg', label: 'GeeksforGeeks', icon: '👨‍💻' },
  { id: 'naukri', label: 'Naukri Code 360', icon: '🎯' },
  { id: 'achievements', label: 'Achievements', icon: '🏆' },
  { id: 'skills', label: 'Skills', icon: '🔧' }
];

// ==============================
// Sub-panels for each tab
// ==============================
const LeetCodePanel = ({ stats }) => {
  const languages = stats.languages.join(', ');
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <motion.div className="col-span-1 md:col-span-2 rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">LeetCode Overview</h3>
            <p className="text-gray-500 mt-1">Daily practice, contest participation and cumulative progress.</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Ranking</div>
            <div className="text-lg font-semibold text-gray-900">{stats.ranking}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500">Solved</div>
                <div className="text-2xl font-bold text-gray-900">
                  <AnimatedCounter to={stats.totalSolved} duration={900} className="inline-block" />
                </div>
                <div className="text-xs text-gray-400 mt-1">Submissions: {formatNumber(stats.submissions)}</div>
              </div>
              <div>
                <Sparkline values={stats.history} width={120} />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Easy</div>
                <div className="text-lg font-semibold text-gray-900">{stats.easy}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Medium</div>
                <div className="text-lg font-semibold text-gray-900">{stats.medium}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Hard</div>
                <div className="text-lg font-semibold text-gray-900">{stats.hard}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Streak</div>
                <div className="text-lg font-semibold text-gray-900">{stats.streak} days</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Languages</div>
            <div className="text-sm text-gray-900 mt-1">{languages}</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Accuracy</div>
            <div className="text-sm text-gray-900 mt-1">{stats.accuracy}%</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Contests</div>
            <div className="text-sm text-gray-900 mt-1">{stats.contests}</div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h4 className="text-md font-semibold text-gray-900">Practice Plan</h4>
        <p className="text-gray-500 text-sm mt-2">A short plan to keep the streak and diversify problem difficulty.</p>
        <ol className="mt-4 space-y-3 text-gray-600 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-cyan-500 mt-1">•</span>
            <span>Spend 30-45 mins on problem solving daily</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-500 mt-1">•</span>
            <span>Alternate between easy/medium each day</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-500 mt-1">•</span>
            <span>Participate in 1 contest every 2 weeks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-500 mt-1">•</span>
            <span>Review incorrect problems and write short notes</span>
          </li>
        </ol>

        <div className="mt-4">
          <a 
            href="https://leetcode.com/u/V1VEK_/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full px-4 py-2 rounded-md bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-colors flex items-center justify-center gap-2"
          >
            <span>View Full Profile</span>
            <span>🔗</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

const GFGPanel = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <motion.div className="col-span-1 md:col-span-2 rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">GeeksforGeeks Profile</h3>
            <p className="text-gray-500 mt-1">Comprehensive DSA practice and coding excellence.</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Coding Score</div>
            <div className="text-lg font-semibold text-gray-900">{stats.codingScore}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500">Problems Solved</div>
                <div className="text-2xl font-bold text-gray-900">
                  <AnimatedCounter to={stats.totalSolved} duration={900} className="inline-block" />
                </div>
                <div className="text-xs text-gray-400 mt-1">Ranking: {stats.ranking}</div>
              </div>
              <div>
                <Sparkline values={stats.history} width={120} />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">School</div>
                <div className="text-lg font-semibold text-gray-900">{stats.school}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Basic</div>
                <div className="text-lg font-semibold text-gray-900">{stats.basic}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Easy</div>
                <div className="text-lg font-semibold text-gray-900">{stats.easy}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Medium</div>
                <div className="text-lg font-semibold text-gray-900">{stats.medium}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200 col-span-2">
                <div className="text-xs text-gray-500">Hard</div>
                <div className="text-lg font-semibold text-gray-900">{stats.hard}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Articles Published</div>
            <div className="text-sm text-gray-900 mt-1">{stats.articles}</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Contests</div>
            <div className="text-sm text-gray-900 mt-1">{stats.contests}</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Current Streak</div>
            <div className="text-sm text-gray-900 mt-1">{stats.streak} days</div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h4 className="text-md font-semibold text-gray-900">GFG Achievements</h4>
        <p className="text-gray-500 text-sm mt-2">Milestones and accomplishments on GeeksforGeeks.</p>
        <ul className="mt-4 space-y-3 text-gray-600 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-emerald-500 mt-1">🏆</span>
            <span>Top 10% among active coders</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500 mt-1">📝</span>
            <span>Published 8 technical articles</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500 mt-1">🎯</span>
            <span>5 contest participations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500 mt-1">⭐</span>
            <span>Consistent 45-day streak</span>
          </li>
        </ul>

        <div className="mt-4">
          <a 
            href="https://www.geeksforgeeks.org/profile/vivekkuy8ju4" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full px-4 py-2 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium hover:opacity-90 transition-colors flex items-center justify-center gap-2"
          >
            <span>View Full Profile</span>
            <span>🔗</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

const NaukriPanel = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <motion.div className="col-span-1 md:col-span-2 rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Naukri Code 360</h3>
            <p className="text-gray-500 mt-1">Advanced coding challenges and skill assessments.</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Ranking</div>
            <div className="text-lg font-semibold text-gray-900">{stats.ranking}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500">Total Solved</div>
                <div className="text-2xl font-bold text-gray-900">
                  <AnimatedCounter to={stats.totalSolved} duration={900} className="inline-block" />
                </div>
                <div className="text-xs text-gray-400 mt-1">Accuracy: {stats.accuracy}%</div>
              </div>
              <div>
                <Sparkline values={stats.history} width={120} />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Easy</div>
                <div className="text-lg font-semibold text-gray-900">{stats.easy}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200">
                <div className="text-xs text-gray-500">Medium</div>
                <div className="text-lg font-semibold text-gray-900">{stats.medium}</div>
              </div>
              <div className="bg-white p-2 rounded border border-gray-200 col-span-2">
                <div className="text-xs text-gray-500">Hard</div>
                <div className="text-lg font-semibold text-gray-900">{stats.hard}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Certifications</div>
            <div className="text-sm text-gray-900 mt-1">{stats.certifications}</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Challenges</div>
            <div className="text-sm text-gray-900 mt-1">{stats.challenges}</div>
          </div>
          <div className="rounded-lg p-4 bg-gray-50">
            <div className="text-xs text-gray-500">Streak</div>
            <div className="text-sm text-gray-900 mt-1">{stats.streak} days</div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h4 className="text-md font-semibold text-gray-900">Skill Assessment</h4>
        <p className="text-gray-500 text-sm mt-2">Performance across different coding domains.</p>
        
        <div className="mt-6">
          <ProgressBar label="Data Structures" value={88} animated={true} />
          <div className="mt-3" />
          <ProgressBar label="Algorithms" value={85} animated={true} />
          <div className="mt-3" />
          <ProgressBar label="System Design" value={78} animated={true} />
          <div className="mt-3" />
          <ProgressBar label="Database" value={82} animated={true} />
        </div>
        
        <div className="mt-4">
          <a 
            href="https://www.naukri.com/code360/profile/a8633cfc-5415-43ef-9204-6197a2e474df" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full px-4 py-2 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-colors flex items-center justify-center gap-2"
          >
            <span>View Full Profile</span>
            <span>🔗</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

const AchievementsPanel = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    <motion.div 
      className="md:col-span-2 rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900">Key Figures</h3>
      <p className="text-gray-500 mt-1">Metrics and milestones gathered over my journey.</p>

      <div className="mt-6 grid grid-cols-2 gap-4">
        {items.map((it) => (
          <motion.div 
            key={it.id} 
            className="rounded-lg p-4 bg-gray-50 flex items-center gap-4 hover:bg-gray-100 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            <div className="text-3xl">{it.icon}</div>
            <div>
              <div className="text-sm text-gray-500">{it.title}</div>
              <div className="text-xl font-semibold text-gray-900">
                <AnimatedCounter to={it.count} duration={700} prefix={it.icon === '💼' ? '' : ''} suffix={it.icon === '💼' ? '+ years' : '+'} />
              </div>
              <div className="text-xs text-gray-400">{it.description}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>

    <motion.div 
      className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <h4 className="text-md font-semibold text-gray-900">Recognition</h4>
      <p className="text-gray-500 mt-2">Selected awards, publications and recognitions.</p>
      <ul className="mt-4 space-y-3 text-gray-600 text-sm">
        <li className="flex items-start gap-2">
          <span className="text-cyan-500 mt-1">•</span>
          <span>Best ML Project — Campus Hack 2022</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan-500 mt-1">•</span>
          <span>First Prize — Smart Farming Challenge 2023</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan-500 mt-1">•</span>
          <span>2x Research Publications in peer-reviewed venues</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan-500 mt-1">•</span>
          <span>Featured Developer — GitHub Spotlight 2023</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan-500 mt-1">•</span>
          <span>Top 10% — Kaggle Competition 2022</span>
        </li>
      </ul>

      <div className="mt-6 flex flex-col gap-3">
        <button className="px-4 py-2 rounded-md bg-cyan-500 text-white font-medium hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2">
          <span>Download CV</span>
          <span>📥</span>
        </button>
        <button className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
          Request References
        </button>
      </div>
    </motion.div>
  </div>
);

const SkillsPanel = ({ skillsData }) => (
  <div className="grid grid-cols-1 gap-6">
    <motion.div 
      className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900">Skill Matrix</h3>
      <p className="text-gray-500 mt-1">Technical competencies across various domains.</p>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {skillsData.map((category) => (
          <motion.div 
            key={category.category} 
            className="rounded-xl p-4 bg-gray-50 border border-gray-200"
            whileHover={{ y: -3 }}
            transition={{ duration: 0.2 }}
          >
            <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-cyan-500">▹</span> {category.category}
            </h4>
            <div className="space-y-3">
              {category.skills.map((skill) => (
                <div key={skill.name} className="group">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 group-hover:text-cyan-600 transition-colors">{skill.name}</span>
                    <span className="text-gray-500">{skill.level}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${skill.level}%` }}
                      transition={{ duration: 1, delay: 0.2 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <motion.div 
        className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <h4 className="text-md font-semibold text-gray-900 mb-4">Skill Radar Visualization</h4>
        <SkillRadarChart skills={[
          { name: 'React', level: 92 },
          { name: 'Node.js', level: 90 },
          { name: 'Python', level: 95 },
          { name: 'ML', level: 88 },
          { name: 'Cloud', level: 85 },
          { name: 'Data', level: 90 }
        ]} size={300} />
      </motion.div>
      
      <motion.div 
        className="rounded-2xl p-6 bg-white shadow-lg border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h4 className="text-md font-semibold text-gray-900 mb-4">Skill Development Timeline</h4>
        <div className="relative pl-8 mt-6 border-l-2 border-gray-200">
          {[
            { year: '2020', skills: ['JavaScript', 'HTML/CSS', 'Python'] },
            { year: '2021', skills: ['React', 'Node.js', 'Express'] },
            { year: '2022', skills: ['TensorFlow', 'AWS', 'Docker'] },
            { year: '2023', skills: ['Kubernetes', 'GraphQL', 'Next.js'] },
            { year: '2024', skills: ['LLMs', 'GenAI', 'Rust'] }
          ].map((item, i) => (
            <div key={i} className="mb-8 relative">
              <div className="absolute -left-11 top-0 w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center border-2 border-cyan-500">
                <div className="text-sm text-cyan-600 font-bold">{item.year}</div>
              </div>
              <div className="ml-4">
                <div className="flex flex-wrap gap-2">
                  {item.skills.map((skill, j) => (
                    <span key={j} className="px-3 py-1 rounded-full text-sm bg-cyan-50 text-cyan-600 border border-cyan-200">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

// ==============================
// Main Component — Figures
// ==============================
export default function Figures() {
  const [activeTab, setActiveTab] = useState('leetcode');
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative min-h-screen py-12 px-4 sm:px-6 lg:px-8 overflow-hidden bg-white" ref={containerRef}>
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      
      <motion.div 
        className="absolute top-10 left-10 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"
        animate={{ x: [0, 20, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
      />
      
      <motion.div 
        className="absolute bottom-20 right-20 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl"
        animate={{ x: [0, -30, 0], y: [0, 30, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
      />
      
      <motion.div 
        className="absolute top-1/3 right-1/4 w-40 h-40 bg-blue-400/5 rounded-full blur-3xl"
        animate={{ x: [0, 40, 0], y: [0, 40, 0] }}
        transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.header 
          className={`text-center mb-12 sticky top-4 z-20 transition-all duration-300 ${isScrolled ? 'pt-4' : ''}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        > 
          <motion.div
            className={`rounded-2xl backdrop-blur-lg border border-gray-200 p-6 transition-all ${isScrolled ? 'bg-white/80 shadow-sm py-4' : 'bg-white/80 shadow-sm'}`}
            animate={{ y: isScrolled ? 0 : 0, scale: isScrolled ? 0.95 : 1 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">Professional Journey</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              My journey, accomplishments, and continuous growth in technology — interactive and data-rich.
            </p>
          </motion.div>
        </motion.header>

        <motion.div 
          className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 sticky top-24 z-20 backdrop-blur-lg rounded-2xl p-4 border border-gray-200 transition-all ${isScrolled ? 'bg-white/80 shadow-sm' : 'bg-white/80 shadow-sm'}`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 overflow-x-auto pb-2 md:pb-0">
            {tabs.map((t) => (
              <motion.button 
                key={t.id} 
                onClick={() => setActiveTab(t.id)} 
                {...scaleHover}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-250 relative min-w-max ${activeTab === t.id ? 'bg-cyan-500 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
                {activeTab === t.id && (
                  <motion.div 
                    className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-white rounded-t-md" 
                    layoutId="underline" 
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.main 
            key={activeTab} 
            initial={{ opacity: 0, y: 14 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }} 
            transition={{ duration: 0.45 }}
            className="mb-16"
          >
            {activeTab === 'leetcode' && <LeetCodePanel stats={leetcodeStats} />}
            {activeTab === 'gfg' && <GFGPanel stats={gfgStats} />}
            {activeTab === 'naukri' && <NaukriPanel stats={naukriStats} />}
            {activeTab === 'achievements' && <AchievementsPanel items={achievements} />}
            {activeTab === 'skills' && <SkillsPanel skillsData={skillsData} />}
          </motion.main>
        </AnimatePresence>
      </div>
    </div>
  );
}